<script setup>
import { computed, useSlots, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';

import Button from 'dashboard/components-next/button/Button.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    default: () => ({}),
  },
  isUpdating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['goToContactsList', 'toggleBlock']);

const { t } = useI18n();
const slots = useSlots();
const route = useRoute();

const isContactSidebarOpen = ref(false);

const contactId = computed(() => route.params.contactId);

const selectedContactName = computed(() => {
  return props.selectedContact?.name;
});

const breadcrumbItems = computed(() => {
  const items = [
    {
      label: t('CONTACTS_LAYOUT.HEADER.BREADCRUMB.CONTACTS'),
      link: '#',
    },
  ];
  if (props.selectedContact) {
    items.push({
      label: selectedContactName.value,
    });
  }
  return items;
});

const isContactBlocked = computed(() => {
  return props.selectedContact?.blocked;
});

const handleBreadcrumbClick = () => {
  emit('goToContactsList');
};

const toggleBlock = () => {
  emit('toggleBlock', isContactBlocked.value);
};

const handleConversationSidebarToggle = () => {
  isContactSidebarOpen.value = !isContactSidebarOpen.value;
};

const closeMobileSidebar = () => {
  if (!isContactSidebarOpen.value) return;
  isContactSidebarOpen.value = false;
};
</script>

<template>
  <section
    class="contacts-details-layout flex w-full h-full overflow-hidden justify-evenly bg-n-background"
  >
    <div
      class="flex flex-col w-full h-full transition-all duration-300 ltr:2xl:ml-56 rtl:2xl:mr-56"
    >
      <header class="contacts-details-header sticky top-0 z-10 px-6 3xl:px-0">
        <div class="w-full mx-auto max-w-[72rem]">
          <div
            class="flex flex-col xs:flex-row items-start xs:items-center justify-between w-full py-7 gap-2"
          >
            <div class="flex items-center gap-2">
              <Button
                icon="i-lucide-arrow-left"
                variant="ghost"
                color="slate"
                size="sm"
                class="!w-8 !h-8"
                aria-label="Back"
                @click="handleBreadcrumbClick"
              />
              <Breadcrumb
                :items="breadcrumbItems"
                @click="handleBreadcrumbClick"
              />
            </div>
            <div class="flex items-center gap-2">
              <Button
                :label="
                  !isContactBlocked
                    ? $t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT')
                    : $t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT')
                "
                size="sm"
                slate
                :is-loading="isUpdating"
                :disabled="isUpdating"
                @click="toggleBlock"
              />
              <VoiceCallButton
                :contact-id="contactId"
                :phone="selectedContact?.phoneNumber"
                :label="$t('CONTACT_PANEL.CALL')"
                size="sm"
              />
              <ComposeConversation :contact-id="contactId">
                <template #trigger="{ toggle }">
                  <Button
                    :label="$t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
                    color="blue"
                    size="sm"
                    class="tm__btn tm__btn--primary"
                    @click="toggle"
                  />
                </template>
              </ComposeConversation>
            </div>
          </div>
        </div>
      </header>
      <main class="flex-1 px-6 overflow-y-auto 3xl:px-px">
        <div class="w-full py-4 mx-auto max-w-[72rem]">
          <div class="contacts-details-card">
            <slot name="default" />
          </div>
        </div>
      </main>
    </div>

    <!-- Desktop sidebar -->
    <div
      v-if="slots.sidebar"
      class="contacts-details-sidebar hidden lg:block overflow-y-auto justify-end min-w-52 w-full py-6 max-w-md border-l border-n-weak bg-n-solid-2"
    >
      <slot name="sidebar" />
    </div>

    <!-- Mobile sidebar container -->
    <div
      v-if="slots.sidebar"
      class="lg:hidden fixed top-0 ltr:right-0 rtl:left-0 h-full z-50 flex justify-end transition-all duration-200 ease-in-out"
      :class="isContactSidebarOpen ? 'w-full' : 'w-16'"
    >
      <!-- Toggle button -->
      <div
        v-on-click-outside="[
          closeMobileSidebar,
          { ignore: ['#contact-sidebar-content'] },
        ]"
        class="flex items-start p-1 w-fit h-fit relative order-1 xs:top-24 top-28 transition-all bg-n-solid-2 border border-n-weak duration-500 ease-in-out"
        :class="[
          isContactSidebarOpen
            ? 'justify-end ltr:rounded-l-full rtl:rounded-r-full ltr:rounded-r-none rtl:rounded-l-none'
            : 'justify-center rounded-full ltr:mr-6 rtl:ml-6',
        ]"
      >
        <Button
          ghost
          slate
          sm
          class="!rounded-full rtl:rotate-180"
          :class="{ 'bg-n-alpha-2': isContactSidebarOpen }"
          :icon="
            isContactSidebarOpen
              ? 'i-lucide-panel-right-close'
              : 'i-lucide-panel-right-open'
          "
          data-contact-sidebar-toggle
          @click="handleConversationSidebarToggle"
        />
      </div>

      <Transition
        enter-active-class="transition-transform duration-200 ease-in-out"
        leave-active-class="transition-transform duration-200 ease-in-out"
        enter-from-class="ltr:translate-x-full rtl:-translate-x-full"
        enter-to-class="ltr:translate-x-0 rtl:-translate-x-0"
        leave-from-class="ltr:translate-x-0 rtl:-translate-x-0"
        leave-to-class="ltr:translate-x-full rtl:-translate-x-full"
      >
        <div
          v-if="isContactSidebarOpen"
          id="contact-sidebar-content"
          class="order-2 w-[85%] sm:w-[50%] bg-n-solid-2 ltr:border-l rtl:border-r border-n-weak overflow-y-auto py-6 shadow-lg"
        >
          <slot name="sidebar" />
        </div>
      </Transition>
    </div>
  </section>
</template>

<style scoped>
.contacts-details-layout {
  background: #ffffff !important;
  color: #111827;

  /* Force contacts pages to always use light WLAB palette */
  --wlab-bg-primary: #ffffff;
  --wlab-bg-secondary: #f8fafc;
  --wlab-bg-tertiary: #f1f5f9;
  --wlab-bg-input: #ffffff;
  --wlab-bg-hover: #f1f5f9;
  --wlab-bg-card: #ffffff;
  --wlab-bg-sidebar: #f1f5f9;
  --wlab-bg-sidebar-inner: #ffffff;

  --wlab-text-primary: #0f172a;
  --wlab-text-secondary: #475569;
  --wlab-text-tertiary: #64748b;
  --wlab-text-muted: #94a3b8;
  --wlab-text-inverse: #ffffff;

  --wlab-border-default: #e2e8f0;
  --wlab-border-light: #f1f5f9;
  --wlab-border-focus: #0066ff;
}

.contacts-details-header {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.contacts-details-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

@media (min-width: 1536px) {
  /* Reduce excessive whitespace on ultra-wide screens */
  .contacts-details-layout > div {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
}

.contacts-details-sidebar {
  background: #f8fafc !important;
}

.contacts-details-layout :deep(.bg-n-background),
.contacts-details-layout :deep(.bg-n-solid-1),
.contacts-details-layout :deep(.dark\:bg-slate-900),
.contacts-details-layout :deep(.dark\:bg-slate-800) {
  background: #ffffff !important;
}

.contacts-details-layout :deep(.bg-n-solid-2) {
  background: #f8fafc !important;
}

.contacts-details-layout :deep(.bg-n-solid-3) {
  background-color: #ffffff !important;
}

.contacts-details-layout :deep(.dark\:bg-n-solid-2) {
  background-color: #f0f2fb !important;
}

.contacts-details-layout :deep(.bg-n-alpha-black2),
.contacts-details-layout :deep(.bg-n-alpha-3) {
  background: #ffffff00 !important;
}

.contacts-details-layout :deep(button[class*='bg-orange-']) {
  background-color: #000000 !important;
  box-shadow: none !important;
}

.contacts-details-layout :deep(button[class*='bg-orange-']:hover),
.contacts-details-layout :deep(button[class*='bg-orange-']:focus-visible) {
  background-color: #000000 !important;
  box-shadow: none !important;
}

.contacts-details-layout :deep(.bg-n-alpha-2),
.contacts-details-layout :deep(.hover\:bg-n-alpha-2:hover) {
  background: #f1f5f9 !important;
}

.contacts-details-layout :deep(.border-n-weak),
.contacts-details-layout :deep(.border-n-strong) {
  border-color: #e5e7eb !important;
}

.contacts-details-layout :deep(.text-n-slate-12) {
  color: #111827 !important;
}

.contacts-details-layout :deep(.text-n-slate-11),
.contacts-details-layout :deep(.text-n-slate-10),
.contacts-details-layout :deep(.text-n-slate-9) {
  color: #64748b !important;
}
</style>
